import React from "react";
import { useReducer, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/primitives";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { PasswordForm } from "./components/PasswordForm";
import { getRandomBackground } from "./constants";
import { passwordReducer, createInitialState } from "./passwordReducer";
import { useAuth } from "@/contexts/AuthContext";

export function ResetPasswordPage() {
	const navigate = useNavigate();
	const { profile, loading: authLoading } = useAuth();
	const [state, dispatch] = useReducer(
		passwordReducer,
		createInitialState("reset"),
	);

	useEffect(() => {
		dispatch({ type: "SET_BACKGROUND", payload: getRandomBackground() });
	}, []);

	// Check for expired/invalid link errors
	useEffect(() => {
		try {
			const sp = new URLSearchParams(window.location.search);
			const redirectParam = sp.get("redirect");
			let err = sp.get("error_code") || sp.get("error") || "";
			if (redirectParam) {
				const u = new URL(redirectParam, window.location.origin);
				const inner = u.searchParams;
				err = inner.get("error_code") || inner.get("error") || err;
			}

			if (err) {
				console.log("ResetPasswordPage: link error detected:", err);
				if (err.includes("otp_expired")) {
					dispatch({
						type: "SET_LINK_ERROR",
						payload:
							"This password reset link has expired. Please request a new one from the login page.",
					});
				} else if (err.includes("email_link_invalid")) {
					dispatch({
						type: "SET_LINK_ERROR",
						payload:
							"This password reset link is invalid. Please request a new one from the login page.",
					});
				} else if (err.includes("access_denied")) {
					dispatch({
						type: "SET_LINK_ERROR",
						payload:
							"Access denied. Please request a new password reset link from the login page.",
					});
				} else {
					dispatch({
						type: "SET_LINK_ERROR",
						payload:
							"There was an error with your password reset link. Please request a new one from the login page.",
					});
				}
			}
		} catch (e) {
			console.error("Error checking for link errors:", e);
		}
	}, []);

	const handleSetNewPassword = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (state.newPassword.length < 8) {
			dispatch({
				type: "SET_PASSWORD_ERROR",
				payload: "Password must be at least 8 characters long.",
			});
			return;
		}

		if (state.newPassword !== state.confirmNewPassword) {
			dispatch({
				type: "SET_PASSWORD_ERROR",
				payload: "Passwords do not match.",
			});
			return;
		}

		dispatch({ type: "SET_PASSWORD_START" });
		try {
			console.log("ResetPasswordPage: updating password...");
			const { error } = await supabase.auth.updateUser({
				password: state.newPassword,
			});

			if (error) {
				console.error("ResetPasswordPage: updateUser error", error);
				dispatch({
					type: "SET_PASSWORD_ERROR",
					payload: error.message || "Failed to update password.",
				});
			} else {
				dispatch({ type: "SET_PASSWORD_SUCCESS" });
			}
		} catch (err) {
			dispatch({
				type: "SET_PASSWORD_ERROR",
				payload: "An unexpected error occurred.",
			});
		}
	};

	return (
		<div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
			{/* Left Panel - Background */}
			<div
				className="hidden lg:flex lg:w-1/2 items-center justify-center p-12 relative bg-cover bg-center bg-no-repeat"
				style={{
					backgroundImage: `url('${state.backgroundImage}')`,
				}}
			>
				<div className="absolute inset-0 bg-black/20"></div>
			</div>

			{/* Right Panel - Forms */}
			<div className="w-full lg:w-1/2 flex flex-col justify-between p-8">
				<div className="flex-1 flex items-center justify-center">
					<div className="w-full max-w-md">
						{/* Back to Login Button */}
						<button
							onClick={() => navigate("/login")}
							className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary mb-6 transition-colors"
						>
							<ArrowLeft className="w-4 h-4" />
							Back to login
						</button>

						{state.view === "success" ? (
							/* Password Updated Success */
							<Card>
								<CardHeader>
									<CardTitle className="flex items-center gap-2">
										<div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
											<CheckCircle2 className="w-5 h-5 text-green-600" />
										</div>
										Password updated successfully
									</CardTitle>
									<CardDescription>
										Your password has been updated. You can now sign in with
										your new password.
									</CardDescription>
								</CardHeader>
								<CardContent>
									<Button
										className="w-full"
										onClick={() => {
											if (profile?.user_type === "mentor") {
												navigate("/mentor", { replace: true });
											} else {
												navigate("/", { replace: true });
											}
										}}
									>
										Go to the app
									</Button>
								</CardContent>
							</Card>
						) : (
							/* Set New Password Form (view === "reset") */
							<Card>
								<CardHeader>
									<CardTitle>Set a new password</CardTitle>
									<CardDescription>
										Enter and confirm your new password to complete the reset.
									</CardDescription>
								</CardHeader>
								<CardContent>
									{state.linkError && (
										<div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
											<p className="text-sm text-red-800 dark:text-red-200 font-medium mb-2">
												Link Error
											</p>
											<p className="text-sm text-red-700 dark:text-red-300">
												{state.linkError}
											</p>
										</div>
									)}
									<PasswordForm
										newPassword={state.newPassword}
										confirmNewPassword={state.confirmNewPassword}
										showNewPassword={state.showNewPassword}
										showConfirmPassword={state.showConfirmPassword}
										isLoading={state.setPwLoading}
										error={state.setPwError}
										onPasswordChange={(value) =>
											dispatch({ type: "SET_NEW_PASSWORD", payload: value })
										}
										onConfirmPasswordChange={(value) =>
											dispatch({ type: "SET_CONFIRM_PASSWORD", payload: value })
										}
										onToggleShowPassword={() =>
											dispatch({ type: "TOGGLE_SHOW_NEW_PASSWORD" })
										}
										onToggleShowConfirmPassword={() =>
											dispatch({ type: "TOGGLE_SHOW_CONFIRM_PASSWORD" })
										}
										onSubmit={handleSetNewPassword}
										onCancel={() => navigate("/login")}
										passwordLabel="New password"
										confirmPasswordLabel="Confirm new password"
										submitButtonText="Save new password"
										cancelButtonText="Cancel"
									/>
								</CardContent>
							</Card>
						)}
					</div>
				</div>

				{/* Footer */}
				<div className="flex flex-col items-center justify-center mt-8 pt-4">
					<img
						src="/img/clarity-insights-logo.png"
						alt="Clarity Insights Logo"
						className="h-12 mb-2 opacity-60"
					/>
					<p className="text-xs text-gray-500 dark:text-gray-400">
						Copyright{" "}
						<a href="https://www.clarityinsightsgroup.com">
							Clarity Insights Group Ltd
						</a>{" "}
						2025
					</p>
				</div>
			</div>
		</div>
	);
}
